import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { qk } from '@/lib/query-client'
import { useWorkspace } from '@/providers/workspace-provider'
import { useAuth } from '@/providers/auth-provider'
import type { TablesInsert, TablesUpdate } from '@/types/database'
import type { TaskWithRelations } from '@/types/domain'

const PROFILE = 'id,full_name,avatar_url,email'

export const TASK_SELECT = `
  *,
  assignee:profiles!tasks_assignee_id_fkey(${PROFILE}),
  requester:profiles!tasks_requester_id_fkey(${PROFILE}),
  approver:profiles!tasks_approver_id_fkey(${PROFILE}),
  tech_lead:profiles!tasks_tech_lead_id_fkey(${PROFILE}),
  task_labels(labels(id,name,color)),
  task_checklist_items(label,is_done),
  campaign:campaigns(id,name),
  event:events(id,name)
`

type RawTask = TaskWithRelations & { task_checklist_items: { label: string; is_done: boolean }[] }

function normalize(row: RawTask): TaskWithRelations {
  const items = row.task_checklist_items ?? []
  return {
    ...row,
    checklist_count: items.length,
    checklist_done: items.filter((i) => i.is_done).length,
    checklist_items: items,
  }
}

export function useTasks() {
  const { workspaceId } = useWorkspace()
  return useQuery({
    queryKey: qk.tasks(workspaceId ?? 'none'),
    enabled: !!workspaceId,
    queryFn: async (): Promise<TaskWithRelations[]> => {
      const { data, error } = await supabase
        .from('tasks')
        .select(TASK_SELECT)
        .eq('workspace_id', workspaceId!)
        .is('deleted_at', null)
        .order('position', { ascending: true })
        .returns<RawTask[]>()
      if (error) throw error
      return (data ?? []).map(normalize)
    },
  })
}

export function useTask(taskId: string | null) {
  return useQuery({
    queryKey: qk.task(taskId ?? 'none'),
    enabled: !!taskId,
    queryFn: async (): Promise<TaskWithRelations> => {
      const { data, error } = await supabase
        .from('tasks')
        .select(TASK_SELECT)
        .eq('id', taskId!)
        .single()
        .returns<RawTask>()
      if (error) throw error
      return normalize(data)
    },
  })
}

function useInvalidate() {
  const qc = useQueryClient()
  const { workspaceId } = useWorkspace()
  return (taskId?: string) => {
    void qc.invalidateQueries({ queryKey: qk.tasks(workspaceId ?? 'none') })
    void qc.invalidateQueries({ queryKey: qk.dashboard(workspaceId ?? 'none') })
    void qc.invalidateQueries({ queryKey: qk.calendar(workspaceId ?? 'none') })
    if (taskId) void qc.invalidateQueries({ queryKey: qk.task(taskId) })
  }
}

export function useCreateTask() {
  const { workspaceId } = useWorkspace()
  const { user } = useAuth()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (input: Omit<TablesInsert<'tasks'>, 'workspace_id' | 'created_by'>) => {
      if (!workspaceId) throw new Error('Nenhum workspace selecionado')
      const { data, error } = await supabase
        .from('tasks')
        .insert({ ...input, workspace_id: workspaceId, created_by: user?.id ?? null })
        .select('id')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(),
  })
}

export function useUpdateTask() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: TablesUpdate<'tasks'> }) => {
      const { error } = await supabase.from('tasks').update(patch).eq('id', id)
      if (error) throw error
      return id
    },
    onSuccess: (id) => invalidate(id),
  })
}

export function useMoveTask() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({
      id,
      status,
      position,
    }: {
      id: string
      status: TablesUpdate<'tasks'>['status']
      position: number
    }) => {
      const { error } = await supabase.from('tasks').update({ status, position }).eq('id', id)
      if (error) throw error
      return id
    },
    onSuccess: (id) => invalidate(id),
  })
}

export function useSoftDeleteTask() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tasks')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
      return id
    },
    onSuccess: () => invalidate(),
  })
}
